import {
  type Configuration,
  LogLevel,
  PublicClientApplication
} from '@azure/msal-browser';
import {
  buildLoginRequest,
  resolveApiScope,
  resolveKnownAuthorities,
  type AuthEnv
} from './msalEnv';

const env = import.meta.env as AuthEnv;

// Today: multi-tenant + personal Microsoft accounts on the `common` authority.
// After the External ID cutover (ADR 0017): a `<tenant>.ciamlogin.com` CIAM
// authority admitting any email or social IdP — no Microsoft account. The
// switch is driven entirely by env values (see msalEnv.ts); the code is
// identical for both, and the API validates either issuer via Identity.Web.
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID,
    authority: import.meta.env.VITE_ENTRA_AUTHORITY,
    // Empty on `common` (MSAL knows login.microsoftonline.com); the CIAM host
    // once VITE_ENTRA_KNOWN_AUTHORITY is set. Required for External ID.
    knownAuthorities: resolveKnownAuthorities(env),
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true
  },
  cache: {
    cacheLocation: 'sessionStorage',  // survives tab refresh, not browser close
    storeAuthStateInCookie: false
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error(`[msal] ${message}`);
        else if (level === LogLevel.Warning) console.warn(`[msal] ${message}`);
      },
      logLevel: LogLevel.Warning
    }
  }
};

// Scope for the API. Defined on the app registration ("Expose an API" ->
// "Add a scope" -> access_as_user). Prefers VITE_ENTRA_API_SCOPE, falls back to
// the legacy VITE_API_SCOPE. NOTE: the scope must stay named `access_as_user` —
// the API's `Sitter` policy checks for that literal scope (see Program.cs).
export const apiScope = resolveApiScope(env);

// prompt: 'select_account' on the workforce flow; dropped under External ID.
export const loginRequest = buildLoginRequest(env);

export const silentRequest = {
  scopes: [apiScope]
};

// Single instance owned by the SPA. Initialize once at boot; React's MsalProvider
// takes this as a prop.
export const msalInstance = new PublicClientApplication(msalConfig);
